/**
 * Something to draw while a session-history read with a NEW range is on the
 * wire, taken from a cached read whose range covers it (`D190`).
 *
 * The ranges are calendar days counted from today — Today's hero reads "the
 * last 42 days", the week card "this week" — so the key moves at midnight and
 * the first open of every day found nothing cached under it: the hero drew its
 * skeleton with yesterday's answer sitting in the cache, until the network
 * came back. Yesterday's 42 days cover today's 41 of them, and the refetch
 * corrects the one day that differs.
 *
 * A cached range COVERS the wanted one when it starts no later and ends no
 * earlier (an open end covers anything; an open-ended wanted range is covered
 * only by another open end). Its sessions are cut to the wanted days by the
 * UTC day of `startedAt`, which is how the server cuts them
 * (`routes/workout.ts`). The freshest covering read wins.
 *
 * Placeholder data is never persisted and never shared: it lives on the
 * observer until the real answer lands.
 *
 * `D194` gives the same covering read to the other two reads keyed by days
 * counted from today: the exercise history (the picker's "last time", the
 * workout's previous sets, the logbook) — whose cached read must also name
 * every exercise wanted, or name none, which means all — and a measurement
 * series (Progress's weight, keyed by the week it starts).
 */
import type {
  ExerciseHistoryOutput,
  MeasurementListOutput,
  SessionListOutput,
  SessionListQuery,
} from "@repo/contract/training";
import type { QueryClient } from "@tanstack/react-query";

interface CachedRange {
  readonly from: string | null;
  readonly to: string | null;
}

function covers(cached: CachedRange, wanted: SessionListQuery): boolean {
  const startsInTime =
    cached.from === null ||
    (wanted.from !== undefined && cached.from <= wanted.from);
  const endsInTime =
    cached.to === null || (wanted.to !== undefined && cached.to >= wanted.to);
  return startsInTime && endsInTime;
}

function isRange(value: unknown): value is CachedRange {
  return (
    typeof value === "object" &&
    value !== null &&
    "from" in value &&
    "to" in value
  );
}

/** The freshest cached read under `prefix` whose key's range covers. */
function freshestCovering<T>(
  client: Pick<QueryClient, "getQueryCache">,
  prefix: readonly unknown[],
  rangeOf: (key: readonly unknown[]) => unknown,
  fits: (range: CachedRange) => boolean
): T | undefined {
  let best: { at: number; data: T } | undefined;
  for (const query of client.getQueryCache().findAll({ queryKey: prefix })) {
    const range = rangeOf(query.queryKey);
    const data = query.state.data as T | undefined;
    if (data === undefined || !isRange(range) || !fits(range)) {
      continue;
    }
    if (best === undefined || query.state.dataUpdatedAt > best.at) {
      best = { at: query.state.dataUpdatedAt, data };
    }
  }
  return best?.data;
}

function inDays(
  instant: string,
  wanted: { readonly from?: string; readonly to?: string }
): boolean {
  const day = instant.slice(0, 10);
  return (
    (wanted.from === undefined || day >= wanted.from) &&
    (wanted.to === undefined || day <= wanted.to)
  );
}

/**
 * The exercise history over a new range, from a cached one that covers its
 * days AND its exercises (none named = every exercise), cut to both.
 */
export function coveringHistory(
  client: Pick<QueryClient, "getQueryCache">,
  wanted: {
    readonly exerciseIds?: readonly string[];
    readonly from?: string;
    readonly to?: string;
  }
): ExerciseHistoryOutput | undefined {
  const ids = wanted.exerciseIds ?? [];
  const found = freshestCovering<ExerciseHistoryOutput>(
    client,
    ["workout", "history"],
    (key) => key[2],
    (range) => {
      const cachedIds = (range as { exerciseIds?: readonly string[] })
        .exerciseIds;
      const named =
        cachedIds === undefined ||
        cachedIds.length === 0 ||
        (ids.length > 0 && ids.every((id) => cachedIds.includes(id)));
      return named && covers(range, wanted);
    }
  );
  if (found === undefined) {
    return;
  }
  return {
    sets: found.sets.filter(
      (set) =>
        (ids.length === 0 || ids.includes(set.exerciseId)) &&
        inDays(set.performedAt, wanted)
    ),
  };
}

/**
 * One measurement series over a new range, from a cached read of the SAME
 * kind whose range covers it. `latest` is the whole series' tail whatever
 * the range (`measurementListOutput`), so it is taken as it stands.
 */
export function coveringMeasurements(
  client: Pick<QueryClient, "getQueryCache">,
  wanted: {
    readonly from?: string;
    readonly kind: string;
    readonly to?: string;
  }
): MeasurementListOutput | undefined {
  const found = freshestCovering<MeasurementListOutput>(
    client,
    ["profile", "measurements", wanted.kind],
    (key) => ({ from: key[3] ?? null, to: key[4] ?? null }),
    (range) => covers(range, wanted)
  );
  if (found === undefined) {
    return;
  }
  return {
    ...found,
    rows: found.rows.filter((row) => inDays(row.measuredAt, wanted)),
  };
}

export function coveringSessions(
  client: Pick<QueryClient, "getQueryCache">,
  wanted: SessionListQuery
): SessionListOutput | undefined {
  let best: { at: number; data: SessionListOutput } | undefined;
  for (const query of client
    .getQueryCache()
    .findAll({ queryKey: ["workout", "sessions"] })) {
    const [, , range] = query.queryKey;
    const data = query.state.data as SessionListOutput | undefined;
    if (data === undefined || !isRange(range) || !covers(range, wanted)) {
      continue;
    }
    if (best === undefined || query.state.dataUpdatedAt > best.at) {
      best = { at: query.state.dataUpdatedAt, data };
    }
  }
  if (best === undefined) {
    return;
  }
  const { from, to } = wanted;
  return {
    sessions: best.data.sessions.filter((session) => {
      const day = session.startedAt.slice(0, 10);
      return (
        (from === undefined || day >= from) && (to === undefined || day <= to)
      );
    }),
  };
}
